import { useState } from 'react';
import { useActivity, type StatsTimeframe } from '../hooks/useHatMail';

const TIMEFRAMES: { value: StatsTimeframe; label: string }[] = [
  { value: 'today', label: 'Today' },
  { value: 'week', label: 'This Week' },
  { value: 'month', label: 'This Month' },
];

const ACTION_LABELS: Record<string, string> = {
  'auth:pin_login': 'PIN login',
  'auth:google_login': 'Google login',
  'card:send': 'Sent',
  'card:batch_send': 'Batch sent',
  'card:edit_send': 'Edited and sent',
  'outbox:cancel': 'Cancelled send',
  'card:redraft': 'Requested redraft',
  'card:escalate': 'Escalated',
  'card:clear': 'Cleared, no reply',
  'card:lock': 'Locked card',
  'card:unlock': 'Unlocked card',
  'feedback:submitted': 'Feedback',
  'spam:rescue': 'Rescued spam',
  'attachment:outbound_uploaded': 'Attached image',
  'attachment:outbound_deleted': 'Removed image',
};

function actionLabel(actionType: string): string {
  return ACTION_LABELS[actionType] ?? actionType.replace(/[:_]/g, ' ');
}

function formatTime(value: string): string {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString([], {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export default function ActivityView() {
  const [timeframe, setTimeframe] = useState<StatsTimeframe>('today');
  const { data, isLoading, error } = useActivity(timeframe);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16 text-gray-400">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-gray-200 border-t-ghw-gold" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="rounded-lg bg-red-50 p-6 text-center">
        <p className="text-sm font-medium text-red-700">Could not load activity</p>
        {error && <p className="mt-1 text-xs text-red-500">{(error as Error).message}</p>}
      </div>
    );
  }

  const summary = [
    { label: 'Logins', value: data.summary.logins },
    { label: 'Sends', value: data.summary.sends + data.summary.edits },
    { label: 'Cancels', value: data.summary.cancels },
    { label: 'Escalations', value: data.summary.escalations },
    { label: 'Redrafts', value: data.summary.redrafts },
    { label: 'Feedback', value: data.summary.feedback },
  ];

  return (
    <div>
      <div className="mb-6 flex items-center gap-1 rounded-lg bg-gray-100 p-1">
        {TIMEFRAMES.map((tf) => (
          <button
            key={tf.value}
            onClick={() => setTimeframe(tf.value)}
            className={`flex-1 rounded-md px-3 py-2 text-xs font-medium transition-colors ${
              timeframe === tf.value
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
            style={{ minHeight: '36px' }}
          >
            {tf.label}
          </button>
        ))}
      </div>

      <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-3">
        {summary.map((item) => (
          <div key={item.label} className="rounded-lg border border-gray-200 bg-white p-4">
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">{item.label}</p>
            <p className="mt-1 text-2xl font-bold text-gray-900">{item.value}</p>
          </div>
        ))}
      </div>

      <div className="mb-6 rounded-lg border border-gray-200 bg-white p-4">
        <h3 className="mb-3 text-sm font-semibold text-gray-900">By Person</h3>
        {data.per_person.length === 0 ? (
          <p className="text-sm text-gray-400">No activity in this period.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 text-xs text-gray-500">
                  <th className="pb-2 pr-4 text-left font-medium">Name</th>
                  <th className="pb-2 px-2 text-left font-medium">Access</th>
                  <th className="pb-2 px-2 text-right font-medium">Logins</th>
                  <th className="pb-2 px-2 text-right font-medium">Sends</th>
                  <th className="pb-2 px-2 text-right font-medium">Edits</th>
                  <th className="pb-2 px-2 text-right font-medium">Cancels</th>
                  <th className="pb-2 px-2 text-right font-medium">Redrafts</th>
                  <th className="pb-2 pl-2 text-right font-medium">Escalations</th>
                </tr>
              </thead>
              <tbody>
                {data.per_person.map((person) => (
                  <tr key={person.actor_key} className="border-b border-gray-50 last:border-0">
                    <td className="py-2 pr-4 font-medium text-gray-800">{person.name}</td>
                    <td className="py-2 px-2 text-gray-500">
                      {person.role === 'foh'
                        ? 'FOH PIN'
                        : person.role === 'admin'
                          ? 'Admin Google'
                          : person.role === 'user'
                            ? 'Google'
                            : 'System'}
                    </td>
                    <td className="py-2 px-2 text-right text-gray-600">{person.logins}</td>
                    <td className="py-2 px-2 text-right text-gray-600">{person.sends}</td>
                    <td className="py-2 px-2 text-right text-gray-600">{person.edits}</td>
                    <td className="py-2 px-2 text-right text-gray-600">{person.cancels}</td>
                    <td className="py-2 px-2 text-right text-gray-600">{person.redrafts}</td>
                    <td className="py-2 pl-2 text-right text-gray-600">{person.escalations}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <h3 className="mb-3 text-sm font-semibold text-gray-900">Recent Events</h3>
        {data.events.length === 0 ? (
          <p className="text-sm text-gray-400">No events in this period.</p>
        ) : (
          <div className="divide-y divide-gray-100">
            {data.events.map((event) => (
              <div key={event.id} className="py-3 first:pt-0 last:pb-0">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900">{actionLabel(event.action_type)}</p>
                    <p className="mt-0.5 truncate text-xs text-gray-500">
                      {event.actor}
                      {event.inbox ? `, ${event.inbox}@` : ''}
                      {event.subject ? `, ${event.subject}` : ''}
                    </p>
                  </div>
                  <span className="shrink-0 text-xs text-gray-400">{formatTime(event.timestamp)}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
